import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { GlobalEvents } from '@highlight/ipc'
import { useAuth } from '@renderer/chat/providers'
import { SKILLS_CHANGED_EVENT } from '@renderer/chat/hooks/useSkills'
import { getChatApiRoute } from '@shared/utils/ChatBackendUtils'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { useUser } from '@renderer/hooks/useUser'

export interface PersonalizationPayload {
  nickname?: string
  occupation?: string
  globalInstructions?: string
  /** Onboarding day-to-day task selections. */
  dayToDay?: Array<string>
  /** Role-backed personas that select the user's skill catalogs. */
  personas?: Array<string> | null
}

export interface PersonalizationData {
  userId: string
  nickname?: string
  occupation?: string
  globalInstructions?: string
  dayToDay?: Array<string> | null
  personas?: Array<string> | null
}

const PERSONALIZATION_QUERY_KEY = 'personalization'

/**
 * Last `role` sent to Amplitude in this renderer process. Several components
 * per window call this hook, so a per-instance guard would send one identify
 * each. Keyed by user so a sign-out/sign-in still identifies the new account.
 */
let syncedRole: { userId: string; role: string } | null = null

export function usePersonalization() {
  const { getAuthToken } = useAuth()
  const queryClient = useQueryClient()
  const userId = useUser()?.id

  // Keyed by user so a sign-out/sign-in never serves the previous account's
  // profile while the new one fetches.
  const { data: personalizationData, isLoading } = useQuery({
    queryKey: [PERSONALIZATION_QUERY_KEY, userId],
    enabled: !!userId,
    queryFn: async () => {
      const url = getChatApiRoute('api/v1/personalizations/me')
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await getAuthToken()}`,
        },
      })
      if (!response.ok) {
        throw new Error('Failed to fetch personalization')
      }
      const responseData = await response.json()
      return responseData.data as PersonalizationData
    },
  })

  // Syncing from the fetched occupation rather than the write site backfills
  // users who chose a role before this property existed, and lets a cleared
  // role clear in Amplitude instead of stranding the old value.
  useEffect(() => {
    if (!personalizationData) {
      return
    }
    const { userId } = personalizationData
    const role = personalizationData.occupation?.trim() || ''
    if (syncedRole?.userId === userId && syncedRole.role === role) {
      return
    }
    syncedRole = { userId, role }
    AmplitudeRenderer.setUserProperties({ role })
  }, [personalizationData])

  const updatePersonalization = useMutation({
    mutationFn: async (payload: PersonalizationPayload) => {
      const url = getChatApiRoute('api/v1/personalizations')
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await getAuthToken()}`,
        },
        body: JSON.stringify(payload),
      })
      if (!response.ok) {
        throw new Error('Failed to update personalization')
      }
      const responseData = await response.json()
      return responseData.data
    },
    onSuccess: (_data, payload) => {
      // The refetch drives the `role` sync, so a rejected write never claims a
      // role the server never took.
      queryClient.invalidateQueries({ queryKey: [PERSONALIZATION_QUERY_KEY] })
      // Occupation initializes personas for legacy users, and personas select
      // the effective skill catalogs. Broadcast either change so every window
      // refetches `skills` and `default-skills`.
      const occupationChanged =
        payload.occupation !== undefined && payload.occupation !== personalizationData?.occupation
      const personasChanged =
        payload.personas !== undefined &&
        JSON.stringify(payload.personas) !== JSON.stringify(personalizationData?.personas)
      if (occupationChanged || personasChanged) {
        GlobalEvents.emit(SKILLS_CHANGED_EVENT)
      }
    },
  })

  return {
    personalizationData,
    isLoading,
    updatePersonalization: updatePersonalization.mutateAsync,
  }
}
